import { t } from 'i18next';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { clsx } from 'clsx';
import { LocalIcon } from './LocalIcon';
import { Bell, Check, CheckCheck, Package, ShoppingBag, Truck, Calendar, Activity, AlertCircle, Info, ChevronRight } from 'lucide-react';
import { useTranslation } from "react-i18next";

interface NotificationBellProps {
    variant?: 'default' | 'floating';
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ variant = 'default' }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    // Close on any click/mousedown outside the container
    React.useEffect(() => {
        const close = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', close);
            document.addEventListener('click', close);
        }
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('click', close);
        };
    }, [isOpen]);

    const handleNotificationClick = (id: string, type?: string) => {
        markAsRead(id, type);
        setIsOpen(false);
    };

    const getNotificationIcon = (msg: string, type?: string) => {
        const message = (msg || '').toLowerCase();
        if (type === 'transfusion' || message.includes('transfus') || message.includes('blood')) return <Activity className="w-4 h-4 text-rose-500" />;
        if (message.includes('deliv')) return <Truck className="w-4 h-4" />;
        if (message.includes('shipp') || message.includes('order')) return <Package className="w-4 h-4" />;
        if (message.includes('appoint') || message.includes('schedule')) return <Calendar className="w-4 h-4" />;
        if (message.includes('health') || message.includes('vitals')) return <Activity className="w-4 h-4" />;
        if (message.includes('alert') || message.includes('urgent')) return <AlertCircle className="w-4 h-4" />;
        return <Info className="w-4 h-4" />;
    };

    return (
        <div className="relative" ref={containerRef}>
            {isOpen && (
                <div
                    className="fixed inset-0 z-[99] bg-slate-900/10 backdrop-blur-[2px] md:hidden"
                    onClick={() => setIsOpen(false)}
                />
            )}

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (window.innerWidth < 768) {
                        navigate('/notifications');
                    } else {
                        setIsOpen(prev => !prev);
                    }
                }}
                className={clsx(
                    "flex items-center justify-center transition-all relative z-[100] group",
                    variant === 'floating' 
                        ? "w-14 h-14 bg-slate-900 text-white rounded-full shadow-xl hover:scale-110 active:scale-95 transition-all duration-300" 
                        : "p-2.5 rounded-xl hover:bg-slate-100/80 active:scale-90",
                    unreadCount > 0 && variant === 'default'
                        ? "text-brand-600 bg-brand-50 shadow-sm shadow-brand-100"
                        : variant === 'default' ? "text-slate-400 bg-slate-50/50" : ""
                )}
            >
                <div className="relative flex items-center justify-center">
                    {variant === 'floating' ? (
                        <Bell size={24} strokeWidth={2} />
                    ) : (
                        <Bell className={clsx("w-5 h-5", unreadCount > 0 ? "animate-swing origin-top" : "")} />
                    )}
                    {unreadCount > 0 && (
                        <span className={clsx(
                            "absolute bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white shadow-xl animate-bounce-subtle",
                            variant === 'floating' ? "-top-3 -right-3 min-w-[22px] h-[22px]" : "-top-2 -right-2 min-w-[18px] h-[18px]"
                        )}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </div>
            </button>

            {isOpen && (
                <div
                    className={clsx(
                        "absolute right-0 w-80 md:w-[420px] backdrop-blur-2xl bg-white/90 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-white/40 z-[120] max-h-[600px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300",
                        variant === 'floating' ? "bottom-20 mb-2 slide-in-from-bottom-10" : "top-14 mt-2 slide-in-from-top-10"
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-6 pb-4 flex items-center justify-between border-b border-slate-100/50">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 leading-tight">{t('ui_notifications_a274f4', `Notifications`)}</h3>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">{t('ui_stay_updated_3624dd', `Stay Updated`)}</p>
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="px-4 py-2 bg-brand-50 text-brand-600 rounded-xl hover:bg-brand-100 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"
                            >
                                <CheckCheck className="w-4 h-4" />
                                {t('ui_clear_all_e77fef', `Clear All`)}</button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="overflow-y-auto flex-1 custom-scrollbar scroll-smooth">
                        {notifications.length === 0 ? (
                            <div className="p-16 text-center">
                                <div className="w-20 h-20 bg-slate-50 flex items-center justify-center rounded-[2.5rem] mx-auto mb-6 text-slate-300">
                                    <Bell className="w-10 h-10" />
                                </div>
                                <h4 className="font-black text-slate-900 mb-1 uppercase tracking-tight">{t('ui_all_caught_up__522f8b', `All Caught Up!`)}</h4>
                                <p className="text-slate-400 text-xs font-bold px-8">{t('ui_no_new_notifica_580dbe', `No new notifications. We'll alert you as soon as something happens.`)}</p>
                            </div>
                        ) : (
                            <div className="p-2 space-y-1">
                                {notifications.map((notification) => {
                                    const isUnread = !notification.read;
                                    const icon = getNotificationIcon(notification.message, notification.type);

                                    return (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification.id, notification.type)}
                                            className={clsx(
                                                "p-4 rounded-[1.5rem] cursor-pointer transition-all duration-300 group",
                                                isUnread 
                                                    ? "bg-brand-50 hover:bg-brand-100/60 shadow-sm border border-brand-100/50" 
                                                    : "hover:bg-slate-50 border border-transparent"
                                            )}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={clsx(
                                                    "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110",
                                                    isUnread ? "bg-white text-brand-600 shadow-sm" : "bg-slate-100 text-slate-400"
                                                )}>
                                                    {icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className={clsx(
                                                            "text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full",
                                                            isUnread ? "bg-brand-600/10 text-brand-600" : "bg-slate-200 text-slate-500"
                                                        )}>
                                                            {notification.type || 'System'}
                                                        </span>
                                                        <span className="text-[10px] font-bold text-slate-400">
                                                            {new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <h4 className={clsx(
                                                        "text-sm font-black truncate mb-0.5",
                                                        isUnread ? "text-slate-900" : "text-slate-600"
                                                    )}>
                                                        {notification.title || 'New Update'}
                                                    </h4>
                                                    <p className={clsx(
                                                        "text-xs leading-relaxed line-clamp-2",
                                                        isUnread ? "text-slate-700 font-medium" : "text-slate-400"
                                                    )}>
                                                        {notification.message.replace('. Tap to view details.', '.')}
                                                    </p>
                                                </div>
                                                {isUnread && (
                                                    <div className="pt-8">
                                                        <ChevronRight size={14} className="text-brand-400" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 bg-slate-50/50 text-center border-t border-slate-100/50">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                            {unreadCount} {t('ui_total_unread_al_f3bacd', `Total Unread Alerts`)}</p>
                    </div>
                </div>
            )}
        </div>
    );
};
